import { useEffect } from "react";
import { Plus, MessageSquare, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useChatPlusStore } from "../store/chatPlusStore";
import * as api from "../services/api";
import { toast } from "sonner";

export function ConversationList() {
  const {
    conversations,
    activeFolderId,
    activeConversationId,
    setActiveConversationId,
    setConversations,
    addConversation,
    removeConversation,
    setIsLoading,
  } = useChatPlusStore();

  useEffect(() => {
    if (activeFolderId) {
      loadConversations();
    } else {
      setConversations([]);
    }
  }, [activeFolderId]);

  const loadConversations = async () => {
    if (!activeFolderId) return;
    
    try {
      setIsLoading(true);
      const data = await api.fetchConversations(activeFolderId);
      setConversations(data);
    } catch (error) {
      console.error("Erro ao carregar conversas:", error);
      toast.error("Erro ao carregar conversas");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateConversation = async () => {
    if (!activeFolderId) {
      toast.error("Selecione uma pasta primeiro");
      return;
    }

    try {
      const conversation = await api.createConversation(activeFolderId);
      addConversation(conversation);
      setActiveConversationId(conversation.id);
      toast.success("Conversa criada");
    } catch (error) {
      console.error("Erro ao criar conversa:", error);
      toast.error("Erro ao criar conversa");
    }
  };

  const handleDeleteConversation = async (id: string) => {
    try {
      await api.deleteConversation(id);
      removeConversation(id);
      toast.success("Conversa excluída");
    } catch (error) {
      console.error("Erro ao excluir conversa:", error);
      toast.error("Erro ao excluir conversa");
    }
  };

  if (!activeFolderId) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p>Selecione uma pasta</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full border-r bg-background">
      <div className="p-4 border-b">
        <Button
          onClick={handleCreateConversation}
          className="w-full"
          size="sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Conversa
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {conversations.map((conversation) => (
          <div
            key={conversation.id}
            className={`flex items-center justify-between p-3 rounded-lg hover:bg-accent cursor-pointer mb-1 ${
              activeConversationId === conversation.id ? 'bg-accent' : ''
            }`}
            onClick={() => setActiveConversationId(conversation.id)}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <MessageSquare className="w-4 h-4 shrink-0" />
              <span className="text-sm truncate">{conversation.title}</span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteConversation(conversation.id);
              }}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}

        {conversations.length === 0 && (
          <div className="text-center text-muted-foreground mt-8">
            <p className="text-sm">Nenhuma conversa ainda</p>
          </div>
        )}
      </div>
    </div>
  );
}
